/**
 * useElectionData Composable
 *
 * Manages election data state, filtering, sorting, and watchlist functionality.
 * Provides reactive data for ElectionGrid and related components.
 */
import { ref, computed, type Ref, type ComputedRef } from 'vue';
import type {
  ElectionContract,
  ElectionType,
  SortOption,
  ViewMode,
  USStateCode,
  PartyCode,
} from '../types/election.types';

// Selection info for state/race selection
export interface SelectionInfo {
  stateCode: USStateCode;
  type?: ElectionType;
  index?: number;
  productConid?: number; // Specific product for race-level deep linking
  candidateConid?: number; // Specific candidate for candidate-level deep linking
  /** Leading party from API ('Democrat', 'Republican', etc.) — absent = toss-up */
  party?: string;
  /** Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid) */
  color?: number;
}

// Order ticket info for opening trade dialog
export interface OrderTicketInfo {
  candidateName: string;
  party: PartyCode;
  side: 'YES' | 'NO' | 'CLOSE';
  price: number;
  electionName: string;
  stateCode: USStateCode;
}

// Return type for the composable
export interface UseElectionDataReturn {
  /** Current election type filter */
  electionType: Ref<ElectionType>;
  /** Current sort option */
  sortBy: Ref<SortOption>;
  /** Current view mode (card/table) */
  viewMode: Ref<ViewMode>;
  /** Price range filter [min, max] in cents */
  priceRange: Ref<[number, number]>;
  /** Which side the price filter applies to ('ANY' = no price filter active) */
  priceFilterSide: Ref<'ANY' | 'DEM' | 'GOP'>;
  /** Whether to show only watchlist items */
  watchlistOnly: Ref<boolean>;
  /** Set of watched state codes */
  watchlist: Ref<Set<USStateCode>>;
  /** Visible race types for "All By State" mode */
  visibleTypes: Ref<Set<'Governor' | 'House' | 'Senate'>>;
  /** Filtered and sorted contracts */
  filteredContracts: ComputedRef<ElectionContract[]>;
  /** Summary statistics */
  summaryStats: ComputedRef<{
    total: number;
    gopLeading: number;
    demLeading: number;
    tossups: number;
  }>;
  /** Toggle watchlist for a state */
  toggleWatchlist: (stateCode: USStateCode) => void;
  /** Check if a state is in watchlist */
  isInWatchlist: (stateCode: USStateCode) => boolean;
  /** Toggle visibility of a race type */
  toggleRaceType: (type: 'Governor' | 'House' | 'Senate') => void;
  /** Check if a race type is visible */
  isRaceTypeVisible: (type: 'Governor' | 'House' | 'Senate') => boolean;
  /** Get price for a specific side */
  getPriceForSide: (contract: ElectionContract, side: 'DEM' | 'GOP') => number;
  /** Check if a contract is in the price range */
  isInPriceRange: (contract: ElectionContract) => boolean;
  /** Map UI sort options to API sorting parameters */
  mapSortToApiParams: (sortOption: SortOption) => {
    product: 'name' | 'open_interest';
  };
}

/**
 * Composable for managing election data filtering and sorting
 *
 * @param initialContracts - Initial array of election contracts
 * @example
 * ```vue
 * <script setup>
 * const { filteredContracts, sortBy, electionType } = useElectionData(contracts)
 * </script>
 * ```
 */
export function useElectionData(
  initialContracts: Ref<ElectionContract[]> | ElectionContract[] = [],
): UseElectionDataReturn {
  // Convert to ref if needed
  const contractsRef = Array.isArray(initialContracts) ? ref(initialContracts) : initialContracts;

  // Filter and sort state
  const electionType = ref<ElectionType>('Governor');
  const sortBy = ref<SortOption>('alphabetical');
  const viewMode = ref<ViewMode>('card');
  const priceRange = ref<[number, number]>([0, 100]);
  const priceFilterSide = ref<'ANY' | 'DEM' | 'GOP'>('ANY');
  const watchlistOnly = ref(false);
  const watchlist = ref<Set<USStateCode>>(new Set());
  const visibleTypes = ref<Set<'Governor' | 'House' | 'Senate'>>(new Set());

  /**
   * Get price for a specific side (DEM or GOP)
   */
  function getPriceForSide(contract: ElectionContract, side: 'DEM' | 'GOP'): number {
    if (side === 'DEM') {
      return contract.favorite === 'DEM' ? contract.price : 100 - contract.price;
    } else {
      return contract.favorite === 'GOP' ? contract.price : 100 - contract.price;
    }
  }

  /**
   * Check if a contract is within the price range filter
   */
  function isInPriceRange(contract: ElectionContract): boolean {
    const side: 'DEM' | 'GOP' =
      priceFilterSide.value === 'ANY' ? (contract.favorite === 'GOP' ? 'GOP' : 'DEM') : priceFilterSide.value;
    const price = getPriceForSide(contract, side);
    return price >= priceRange.value[0] && price <= priceRange.value[1];
  }

  /**
   * Map UI sort options to API sorting parameters
   */
  function mapSortToApiParams(sortOption: SortOption): {
    product: 'name' | 'open_interest';
  } {
    switch (sortOption) {
      case 'alphabetical':
        return { product: 'name' };
      case 'open-interest':
        return { product: 'open_interest' };
      default:
        return { product: 'open_interest' };
    }
  }

  /**
   * Filtered contracts (sorting and price filtering now handled by API)
   */
  const filteredContracts = computed<ElectionContract[]>(() => {
    let result = [...contractsRef.value];

    // Filter by watchlist (client-side filtering still needed)
    if (watchlistOnly.value) {
      result = result.filter((c) => watchlist.value.has(c.stateCode));
    }

    // Note: Sorting and price filtering are now handled server-side via API
    // The contractsRef should contain pre-sorted and pre-filtered data from the API

    return result;
  });

  /**
   * Summary statistics for the current data
   * Note: For API-driven components, statistics should be computed from API response data
   */
  const summaryStats = computed(() => {
    const contracts = filteredContracts.value;
    return {
      total: contracts.length,
      gopLeading: contracts.filter((c) => c.favorite === 'GOP').length,
      demLeading: contracts.filter((c) => c.favorite === 'DEM').length,
      tossups: contracts.filter((c) => c.price >= 45 && c.price <= 55).length,
    };
  });

  /**
   * Toggle watchlist for a state
   */
  function toggleWatchlist(stateCode: USStateCode): void {
    const newSet = new Set(watchlist.value);
    if (newSet.has(stateCode)) {
      newSet.delete(stateCode);
    } else {
      newSet.add(stateCode);
    }
    watchlist.value = newSet;
  }

  /**
   * Check if a state is in watchlist
   */
  function isInWatchlist(stateCode: USStateCode): boolean {
    return watchlist.value.has(stateCode);
  }

  /**
   * Toggle visibility of a race type
   */
  function toggleRaceType(type: 'Governor' | 'House' | 'Senate'): void {
    const newSet = new Set(visibleTypes.value);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    visibleTypes.value = newSet;
  }

  /**
   * Check if a race type is visible
   * Empty set means all types are visible
   */
  function isRaceTypeVisible(type: 'Governor' | 'House' | 'Senate'): boolean {
    return visibleTypes.value.size === 0 || visibleTypes.value.has(type);
  }

  return {
    electionType,
    sortBy,
    viewMode,
    priceRange,
    priceFilterSide,
    watchlistOnly,
    watchlist,
    visibleTypes,
    filteredContracts,
    summaryStats,
    toggleWatchlist,
    isInWatchlist,
    toggleRaceType,
    isRaceTypeVisible,
    getPriceForSide,
    isInPriceRange,
    mapSortToApiParams,
  };
}
